/**
 * Utilitários para formatação e manipulação de datas
 */
export class DateUtils {
  /**
   * Formata data para formato brasileiro (DD/MM/YYYY).
   * Evita bug de timezone ao parsear strings ISO.
   */
  static formatarDataBrasileira(data: Date | string | undefined): string {
    if (!data) return '';
    
    // Se for string no formato ISO (YYYY-MM-DD), extrair diretamente sem conversão de timezone
    if (typeof data === 'string') {
      const isoMatch = data.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (isoMatch) {
        const [, ano, mes, dia] = isoMatch;
        return `${dia}/${mes}/${ano}`;
      }
    }
    
    // Para objetos Date, usar getDate() normalmente
    const date = typeof data === 'string' ? new Date(data) : data;
    if (isNaN(date.getTime())) return '';
    
    const dia = String(date.getDate()).padStart(2, '0');
    const mes = String(date.getMonth() + 1).padStart(2, '0');
    const ano = date.getFullYear();
    
    return `${dia}/${mes}/${ano}`;
  }

  /**
   * Formata data para formato curto (DD/MM). Mesmo tratamento de timezone de formatarDataBrasileira.
   */
  static formatarDataCurta(data: Date | string | undefined): string {
    if (!data) return '';

    if (typeof data === 'string') {
      const isoMatch = data.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (isoMatch) {
        const [, , mes, dia] = isoMatch;
        return `${dia}/${mes}`;
      }
    }

    const date = typeof data === 'string' ? new Date(data) : data;
    if (isNaN(date.getTime())) return '';
    
    const dia = String(date.getDate()).padStart(2, '0');
    const mes = String(date.getMonth() + 1).padStart(2, '0');
    
    return `${dia}/${mes}`;
  }

  /**
   * Parseia string ISO (YYYY-MM-DD) sem conversão de timezone.
   * Retorna { ano, mes, dia } onde mes é 0-indexed (padrão JavaScript).
   * Evita o bug de new Date("YYYY-MM-DD") que interpreta como UTC meia-noite
   * e desloca o dia em fusos UTC- (ex: UTC-3 no Brasil).
   */
  static parseDateISO(dataStr: string): { ano: number; mes: number; dia: number } | null {
    if (!dataStr) return null;
    const match = dataStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return null;
    return {
      ano: parseInt(match[1], 10),
      mes: parseInt(match[2], 10) - 1,
      dia: parseInt(match[3], 10),
    };
  }

  /**
   * Formata data para formato ISO (YYYY-MM-DD) usado pela API
   */
  static formatarDataISO(data: Date): string {
    const ano = data.getFullYear();
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const dia = String(data.getDate()).padStart(2, '0');
    
    return `${ano}-${mes}-${dia}`;
  }

  /**
   * Retorna a segunda-feira da semana ISO à qual a data pertence.
   * Usado para comparar se duas datas estão na mesma semana (Seg-Dom).
   */
  static getSegundaFeiraDaSemana(data: Date): Date {
    const d = new Date(data);
    const diaSemana = d.getDay(); // 0=Dom, 1=Seg, ..., 6=Sáb
    const diasAteSegunda = (diaSemana + 6) % 7;
    d.setDate(d.getDate() - diasAteSegunda);
    return d;
  }

  /**
   * Verifica se duas datas pertencem à mesma semana ISO (Seg-Dom).
   */
  static isSameISOWeek(a: Date, b: Date): boolean {
    const segundaA = DateUtils.getSegundaFeiraDaSemana(a);
    const segundaB = DateUtils.getSegundaFeiraDaSemana(b);
    return segundaA.getTime() === segundaB.getTime();
  }

  /**
   * Verifica se uma data está dentro de um intervalo
   */
  static estaDentroDoIntervalo(data: Date, dataInicial: string | Date, dataFinal: string | Date): boolean {
    const dataVerificada = new Date(data);
    // new Date(stringISO) interpreta datas "YYYY-MM-DD" como meia-noite UTC, que em fusos
    // UTC- (ex: UTC-3 no Brasil) desloca pro dia anterior no horário local — usa parseDateISO
    // (construção local) pra strings, evitando o mesmo bug documentado em parseDateISO acima.
    const inicio = typeof dataInicial === 'string' ? this.parseStringOuData(dataInicial) : dataInicial;
    const fim = typeof dataFinal === 'string' ? this.parseStringOuData(dataFinal) : dataFinal;

    inicio.setHours(0, 0, 0, 0);
    fim.setHours(23, 59, 59, 999);
    dataVerificada.setHours(0, 0, 0, 0);

    return dataVerificada >= inicio && dataVerificada <= fim;
  }

  private static parseStringOuData(dataStr: string): Date {
    const parsed = this.parseDateISO(dataStr);
    return parsed ? new Date(parsed.ano, parsed.mes, parsed.dia) : new Date(dataStr);
  }
}


